import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ClipboardList,
  ArrowLeft,
  Loader2,
  AlertCircle,
  Plus,
  Trash2,
  GripVertical,
  FileText,
  CheckCircle,
  Users,
  Building2,
  UserPlus,
  X,
  Edit2,
  MoveUp,
  MoveDown,
  MapPin,
  Link,
  QrCode,
  Copy,
  Clock,
  Tag,
} from 'lucide-react';
import api from '../lib/api';
import type { Questionnaire, DataField, DataEntry, PaginatedResponse, Cluster } from '../types';
import { DataEntryStatus, QuestionnaireStatus } from '../types';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

const statusLabels: Record<QuestionnaireStatus, string> = {
  [QuestionnaireStatus.Draft]: 'Entwurf',
  [QuestionnaireStatus.Active]: 'Aktiv',
  [QuestionnaireStatus.Archived]: 'Archiviert',
};

const statusColors: Record<QuestionnaireStatus, string> = {
  [QuestionnaireStatus.Draft]: 'badge-warning',
  [QuestionnaireStatus.Active]: 'badge-success',
  [QuestionnaireStatus.Archived]: 'badge-danger',
};

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

function Modal({ isOpen, onClose, title, children }: ModalProps) {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <button onClick={onClose} className="btn-ghost p-1"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">{children}</div>
      </div>
    </div>
  );
}

function FormatValue({ de, navigate }: { de: any, navigate?: (url: string) => void }) {
  if (!de) return <span className="text-gray-400">&mdash;</span>;
  const v = de.value;
  if (v === null || v === undefined) return <span className="text-gray-400">&mdash;</span>;

  // Try parsing aggregated JSON value wrapper
  let parsed: any = null;
  if (typeof v === 'string' && v.startsWith('{') && (v.includes('"value"') || v.includes('"sourceCount"'))) {
    try { parsed = JSON.parse(v); } catch {}
  }
  if (parsed && parsed.value !== undefined) {
    const raw = String(parsed.value);
    const sc = parsed.sourceCount;
    // If the inner value is a POI array
    let innerArr: any = null;
    if (raw.startsWith('[')) { try { innerArr = JSON.parse(raw); } catch {} }

    if (innerArr && Array.isArray(innerArr)) {
      return (
        <div className="space-y-1">
          {innerArr.map((p: any, i: number) => (
            <div key={i} className="flex items-center gap-1.5">
              <span className="text-gray-800">{p.name || p.description || ('Punkt ' + (i+1))}</span>
              {p.lat !== 0 && p.lon !== 0 && navigate && (
                <button
                  onClick={(e) => { e.stopPropagation(); navigate('/maps?lat=' + p.lat + '&lon=' + p.lon); }}
                  className="text-blue-500 hover:text-blue-700 p-0.5"
                  title="Auf Karte anzeigen"
                >
                  <MapPin size={14} />
                </button>
              )}
            </div>
          ))}
          {sc > 1 && <span className="text-xs text-orange-600 font-medium">(aus {sc} Quellen)</span>}
        </div>
      );
    }

    if (sc > 1) {
      return (
        <span className="inline-flex items-center gap-1.5">
          <span className="text-gray-800 font-medium">{raw}</span>
          <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full font-medium">{sc} Quellen</span>
        </span>
      );
    }
    return <span className="text-gray-800">{raw}</span>;
  }

  // Raw POI/Poi JSON-Array
  if (typeof v === 'string' && v.startsWith('[') && (v.includes('"lat"') || v.includes('"name"'))) {
    try {
      const arr = JSON.parse(v);
      if (Array.isArray(arr)) {
        return (
          <div className="space-y-1">
            {arr.map((p: any, i: number) => (
              <div key={i} className="flex items-center gap-1.5">
                <span className="text-gray-800">{p.name || p.description || ('Punkt ' + (i+1))}</span>
                {p.lat !== 0 && p.lon !== 0 && navigate && (
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate('/maps?lat=' + p.lat + '&lon=' + p.lon); }}
                    className="text-blue-500 hover:text-blue-700 p-0.5"
                    title="Auf Karte anzeigen"
                  >
                    <MapPin size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        );
      }
    } catch {}
  }

  const s = typeof v === 'string' ? v : String(v);
  return <span className="text-gray-800 truncate">{s.substring(0, 60)}</span>;
}

export default function QuestionnaireDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showUserAssignModal, setShowUserAssignModal] = useState(false);
  const [showOrgAssignModal, setShowOrgAssignModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'fields' | 'submissions' | 'assignments'>('fields');
  const [assignUserId, setAssignUserId] = useState('');
  const [assignOrgId, setAssignOrgId] = useState('');
  const [assignFieldId, setAssignFieldId] = useState('');
  const [showAnonLinkModal, setShowAnonLinkModal] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const [qrLink, setQrLink] = useState('');
  const [anonExpiry, setAnonExpiry] = useState('');
  const [anonExpiryTime, setAnonExpiryTime] = useState('');

  const { data: questionnaire, isLoading, error } = useQuery<Questionnaire>({
    queryKey: ['questionnaire', id],
    queryFn: async () => {
      const res = await api.get<Questionnaire>(`/questionnaires/${id}`);
      return res.data;
    },
    enabled: !!id,
  });

  const submissions = useQuery<any[]>({
    queryKey: ['questionnaire', id, 'entries'],
    queryFn: async () => {
      const res = await api.get(`/questionnaires/${id}/entries`);
      return Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
    },
    enabled: !!id && activeTab === 'submissions',
  });

  // ─── Field mutations ───

  const removeFieldMutation = useMutation({
    mutationFn: async (qFieldId: string) => {
      await api.delete(`/questionnaires/${id}/fields/${qFieldId}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['questionnaire', id] }),
  });

  const addFieldMutation = useMutation({
    mutationFn: async (fieldId: string) => {
      const currentFields = questionnaire?.fields ?? [];
      await api.post(`/questionnaires/${id}/fields`, {
        fieldId,
        sortOrder: currentFields.length,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['questionnaire', id] });
      setShowAssignModal(false);
      setAssignFieldId('');
    },
  });

  const reorderMutation = useMutation({
    mutationFn: async (fieldIds: string[]) => {
      await api.patch(`/questionnaires/${id}/fields/reorder`, { fieldIds });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['questionnaire', id] }),
  });

  // ─── Assignment mutations ───

  const assignUserMutation = useMutation({
    mutationFn: async () => {
      await api.post(`/questionnaires/${id}/assign`, { userId: assignUserId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['questionnaire', id] });
      setShowUserAssignModal(false);
      setAssignUserId('');
    },
    onError: (err: any) => alert('Fehler beim Zuweisen: ' + (err?.response?.data?.message || err.message)),
  });

  const removeUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      await api.delete(`/questionnaires/${id}/assign/${userId}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['questionnaire', id] }),
    onError: (err: any) => alert('Fehler beim Entfernen: ' + (err?.response?.data?.message || err.message)),
  });

  const assignOrgMutation = useMutation({
    mutationFn: async () => {
      await api.post(`/questionnaires/${id}/assign-org`, { organizationId: assignOrgId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['questionnaire', id] });
      setShowOrgAssignModal(false);
      setAssignOrgId('');
    },
    onError: (err: any) => alert('Fehler beim Zuweisen: ' + (err?.response?.data?.message || err.message)),
  });

  const removeOrgMutation = useMutation({
    mutationFn: async (orgId: string) => {
      await api.delete(`/questionnaires/${id}/assign-org/${orgId}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['questionnaire', id] }),
    onError: (err: any) => alert('Fehler beim Entfernen: ' + (err?.response?.data?.message || err.message)),
  });

  const { data: clustersData } = useQuery<Cluster[]>({
    queryKey: ['clusters'],
    queryFn: async () => {
      const res = await api.get('/clusters');
      return Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
    },
  });
  const clusters = clustersData ?? [];
  // Native selects können Optionen nicht einfärben – der Punkt neben dem Select
  // zeigt daher die Farbe des zuletzt gewählten Clusters (die Auswahl wird
  // direkt als Chip übernommen und das Select zurückgesetzt).
  const [lastClusterPickId, setLastClusterPickId] = useState('');

  const updateClustersMutation = useMutation({
    mutationFn: async (clusterIds: string[]) => {
      await api.patch(`/questionnaires/${id}`, { clusterIds });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['questionnaire', id] }),
  });
  const assignedClusterIds = questionnaire?.clusters?.map((entry) => entry.clusterId) ?? [];
  const setClusters = (clusterIds: string[]) => updateClustersMutation.mutate(clusterIds);
  const addCluster = (clusterId: string) => {
    if (assignedClusterIds.includes(clusterId)) return;
    setClusters([...assignedClusterIds, clusterId]);
  };
  const removeCluster = (clusterId: string) => {
    setClusters(assignedClusterIds.filter((entry) => entry !== clusterId));
  };

  const updateStatusMutation = useMutation({
    mutationFn: async (status: QuestionnaireStatus) => {
      await api.patch(`/questionnaires/${id}`, { status });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['questionnaire', id] }),
  });

  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const res = await api.get('/users', { params: { limit: 500 } });
      return Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
    },
    enabled: showUserAssignModal,
  });

  const { data: orgsData } = useQuery({
    queryKey: ['organizations'],
    queryFn: async () => {
      const res = await api.get('/organizations', { params: { limit: 500 } });
      return Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
    },
    enabled: showOrgAssignModal,
  });

  const { data: fieldsData } = useQuery<any[]>({
    queryKey: ['fields'],
    queryFn: async () => {
      const res = await api.get('/data-fields', { params: { limit: 500 } });
      return Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
    },
    enabled: showAssignModal,
  });

  // ─── Anonymous Link ───

  const { data: anonymousLinks } = useQuery<any[]>({
    queryKey: ['anonymous-links', id],
    queryFn: async () => {
      const res = await api.get(`/questionnaires/${id}/anonymous-links`);
      return Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
    },
    enabled: !!id,
  });

  const createAnonLinkMutation = useMutation({
    mutationFn: async () => {
      const body: any = {};
      if (anonExpiry) {
        body.expiresAt = anonExpiry + (anonExpiryTime ? 'T' + anonExpiryTime + ':00' : 'T23:59:00');
      }
      await api.post(`/questionnaires/${id}/anonymous-links`, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['anonymous-links', id] });
      setShowAnonLinkModal(false);
      setAnonExpiry('');
      setAnonExpiryTime('');
    },
    onError: (err: any) => alert('Fehler: ' + (err?.response?.data?.message || err.message)),
  });

  const deactivateAnonLinkMutation = useMutation({
    mutationFn: async (linkId: string) => {
      await api.delete(`/questionnaires/${id}/anonymous-links/${linkId}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['anonymous-links', id] }),
    onError: (err: any) => alert('Fehler: ' + (err?.response?.data?.message || err.message)),
  });

  // ─── Direktlink (Anmeldung erforderlich) ───

  const { data: directLink, isError: directLinkError } = useQuery<{ id: string; token: string; questionnaireId: string }>({
    queryKey: ['direct-link', id],
    queryFn: async () => {
      const res = await api.get(`/questionnaires/${id}/direct-link`);
      return res.data;
    },
    enabled: !!id && activeTab === 'assignments',
  });

  // ─── Helpers ───

  const reorder = (index: number, direction: 'up' | 'down') => {
    const fields = questionnaire?.fields ?? [];
    if (!fields.length) return;
    const ids = fields.map((f: any) => f.dataFieldId || f.fieldId);
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= ids.length) return;
    [ids[index], ids[newIndex]] = [ids[newIndex], ids[index]];
    reorderMutation.mutate(ids);
  };

  const allFields = Array.isArray(fieldsData) ? fieldsData : (fieldsData?.data ?? []);
  const existingFieldIds = questionnaire?.fields?.map((f: any) => f.dataFieldId || f.fieldId) ?? [];
  const availableFields = allFields.filter((f: any) => !existingFieldIds.includes(f.id));

  const users = Array.isArray(usersData) ? usersData : (usersData?.data ?? []);
  const orgs = Array.isArray(orgsData) ? orgsData : (orgsData?.data ?? []);
  const assignedUserIds = questionnaire?.assignments?.map((a: any) => a.user.id) ?? [];
  const assignedOrgIds = questionnaire?.orgAssignments?.map((a: any) => a.organization.id) ?? [];

  const isAdminGenSek = false;

  // Ein Direktlink pro Fragebogen: absolute URL mit Fragebogen-ID.
  const directLinkUrl = directLink
    ? window.location.origin + '/questionnaires/' + id + '/fill'
    : '';

  // ─── Loading / Error ───

  if (isLoading) {
    return <div className="flex items-center justify-center py-20"><Loader2 size={32} className="animate-spin text-gray-400" /></div>;
  }

  if (error || !questionnaire) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 mb-4">
          <AlertCircle size={18} className="shrink-0" /><span>Fragebogen konnte nicht geladen werden</span>
        </div>
        <button onClick={() => navigate('/questionnaires')} className="btn-secondary"><ArrowLeft size={16} /> Zurück</button>
      </div>
    );
  }

  const fieldTypeLabel = (type: string): string => {
    const labels: Record<string, string> = { TEXT: 'Text', NUMBER: 'Zahl', DECIMAL: 'Dezimal', IMAGE: 'Bild', TIMESTAMP: 'Zeitstempel', COORDINATE: 'Koordinate', POI: 'POI', POI_LIST: 'POI-Liste', COORDINATE_PAIR: 'Koordinatenpaar', COORDINATE_POLYGON: 'Polygon', GEOJSON: 'GeoJSON', BINARY: 'Binär', text: 'Text', number: 'Zahl', boolean: 'Ja/Nein', date: 'Datum', datetime: 'Datum & Zeit', select: 'Auswahl' };
    return labels[type] ?? type;
  };

  // ─── Render ───

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/questionnaires')} className="btn-ghost p-2" title="Zurück"><ArrowLeft size={20} /></button>
          <ClipboardList size={28} className="text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{questionnaire.name}</h1>
            <p className="text-sm text-gray-500">
              v{questionnaire.version} · {questionnaire.organization?.name ?? '—'}
              {questionnaire.allowEditWithinDay ? ' · ✏️ Korrigierbar' : ''}
              {questionnaire.assignments?.length ? ` · ${questionnaire.assignments.length} User` : ''}
              {questionnaire.orgAssignments?.length ? ` · ${questionnaire.orgAssignments.length} OEs` : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={statusColors[questionnaire.status as QuestionnaireStatus]}>
            {statusLabels[questionnaire.status as QuestionnaireStatus]}
          </span>
          {questionnaire.status === QuestionnaireStatus.Draft && (
            <button onClick={() => updateStatusMutation.mutate(QuestionnaireStatus.Active)}
              className="btn-primary text-xs px-3 py-1.5"><CheckCircle size={14} /> Aktivieren</button>
          )}
          {questionnaire.status === QuestionnaireStatus.Active && (
            <button onClick={() => updateStatusMutation.mutate(QuestionnaireStatus.Archived)}
              className="btn-secondary text-xs px-3 py-1.5"><Archive size={14} /> Archivieren</button>
          )}
          <button onClick={async () => {
            await api.patch('/questionnaires/' + id, { allowEditWithinDay: !questionnaire.allowEditWithinDay });
            queryClient.invalidateQueries({ queryKey: ['questionnaire', id] });
          }}
            className={questionnaire.allowEditWithinDay ? 'bg-green-100 text-green-700 text-xs px-3 py-1.5 rounded-lg font-medium' : 'bg-gray-100 text-gray-500 text-xs px-3 py-1.5 rounded-lg font-medium'}
            title="Erlaubt Nutzern, Einreichungen am selben Tag zu bearbeiten"
          >
            <Edit2 size={14} className="inline mr-1" />{questionnaire.allowEditWithinDay ? 'Bearbeitbar' : 'Nicht bearbeitbar'}
          </button>
        </div>
      </div>

      {/* Beschreibung */}
      {questionnaire.description && (
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{questionnaire.description}</p>
        </div>
      )}

      {/* Meta */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        <div className="card"><p className="text-xs text-gray-500 mb-1">Deadline</p><p className="text-sm font-medium">
          {questionnaire.deadline ? format(new Date(questionnaire.deadline), 'dd.MM.yyyy HH:mm', { locale: de }) : 'Keine Frist'}
        </p></div>
        <div className="card"><p className="text-xs text-gray-500 mb-1">Frist-Typ</p><p className="text-sm font-medium">{questionnaire.deadlineType || 'Einmalig'}</p></div>
        <div className="card"><p className="text-xs text-gray-500 mb-1">Zuweisung</p><p className="text-sm font-medium">
          {(function() {
            const hasUsers = questionnaire.assignments?.length > 0;
            const hasOrgs = questionnaire.orgAssignments?.length > 0;
            const hasAnon = anonymousLinks?.filter((l: any) => l.isActive).length > 0;
            const parts: string[] = [];
            if (hasOrgs) parts.push('Organisationen');
            if (hasUsers) parts.push('Mitarbeiter');
            if (hasAnon) parts.push('anonymer Link');
            return parts.length > 0 ? parts.join(' + ') : 'Nicht zugewiesen';
          })()}
        </p></div>
        <div className="card"><p className="text-xs text-gray-500 mb-1">Auto-Submit</p>
          <label className="flex items-center gap-2 text-sm cursor-pointer mt-1">
            <input type="checkbox" checked={questionnaire.autoSubmit || false}
              onChange={async () => {
                try {
                  const newVal = !questionnaire.autoSubmit;
                  await api.patch('/questionnaires/' + id, { autoSubmit: newVal });
                  queryClient.invalidateQueries({ queryKey: ['questionnaire', id] });
                } catch (err: any) {
                  alert('Fehler: ' + (err?.response?.data?.message || err.message));
                }
              }}
              className="checkbox" />
            <span className="text-xs">Auto <span className="text-gray-400">(aggregiert)</span></span>
          </label>
        </div>
        <div className="card"><p className="text-xs text-gray-500 mb-1">Felder / Einreichungen</p><p className="text-sm font-medium">{questionnaire.fields?.length ?? 0} · {questionnaire._count?.entries ?? 0}</p></div>
      </div>

      {/* Cluster */}
      <div className="card">
        <p className="text-xs text-gray-500 mb-2 flex items-center gap-1"><Tag size={12} /> Cluster (Pakete)</p>
        <div className="flex flex-wrap items-center gap-1">
          {assignedClusterIds.length === 0 && (
            <span className="text-xs text-gray-400">Keine Cluster zugewiesen</span>
          )}
          {assignedClusterIds.map((clusterId) => {
            const cluster = clusters.find((entry) => entry.id === clusterId);
            return (
              <span key={clusterId} className="inline-flex items-center gap-1 bg-primary-50 text-primary-800 text-xs font-medium px-2 py-1 rounded-full">
                <span
                  className="h-3 w-3 shrink-0 rounded-full border border-gray-200 ring-1 ring-black/10"
                  style={{ backgroundColor: cluster?.color || '#9ca3af' }}
                  title={cluster?.name}
                  aria-hidden="true"
                />
                {cluster?.name || clusterId}
                <button type="button" onClick={() => removeCluster(clusterId)} disabled={updateClustersMutation.isPending} className="hover:text-red-500 disabled:opacity-50" title="Cluster entfernen">
                  <X size={12} />
                </button>
              </span>
            );
          })}
          <span className="inline-flex items-center gap-1">
            <select
              className="text-xs border rounded px-2 py-1 bg-white ml-1"
              value=""
              disabled={updateClustersMutation.isPending}
              onChange={(e) => { if (e.target.value) { setLastClusterPickId(e.target.value); addCluster(e.target.value); e.target.value = ''; } }}
            >
              <option value="">Cluster auswählen...</option>
              {clusters.filter((cluster) => !assignedClusterIds.includes(cluster.id)).map((cluster) => (
                <option key={cluster.id} value={cluster.id}>{cluster.name}</option>
              ))}
            </select>
            {(() => {
              const picked = clusters.find((cluster) => cluster.id === lastClusterPickId);
              return picked && assignedClusterIds.includes(lastClusterPickId) ? (
                <span
                  className="h-3 w-3 shrink-0 rounded-full border border-gray-200 ring-1 ring-black/10"
                  style={{ backgroundColor: picked.color || '#9ca3af' }}
                  title={picked.name}
                  aria-hidden="true"
                />
              ) : null;
            })()}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex gap-6">
          {(['fields', 'submissions', 'assignments'] as const).map((tab) => (
            <button key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${activeTab === tab ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              {tab === 'fields' ? `Felder (${questionnaire.fields?.length ?? 0})` :
               tab === 'submissions' ? `Einreichungen (${questionnaire._count?.entries ?? 0})` :
               'Zuweisungen'}
            </button>
          ))}
        </div>
      </div>

      {/* ════════════ Felder-Tab ════════════ */}
      {activeTab === 'fields' && (
        <div className="space-y-3">
          <div className="flex justify-end">
            <button onClick={() => setShowAssignModal(true)} className="btn-secondary text-sm">
              <Plus size={14} /> Feld zuweisen
            </button>
          </div>
          {(!questionnaire.fields || questionnaire.fields.length === 0) ? (
            <div className="card text-center py-10">
              <FileText size={40} className="mx-auto text-gray-300 mb-3" />
              <p className="text-sm text-gray-500">Keine Felder zugewiesen</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="data-table w-full">
                <thead>
                  <tr>
                    <th className="w-12">#</th>
                    <th>Feldname</th>
                    <th>Technischer Key</th>
                    <th>Typ</th>
                    <th>Anzeigetext</th>
                    <th>Pflicht</th>
                    <th className="w-24">Reihenfolge</th>
                    <th className="w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {questionnaire.fields.map((qf: any, idx: number) => (
                    <tr key={qf.id || idx} className="hover:bg-gray-50">
                      <td className="text-gray-400 text-sm">{idx + 1}</td>
                      <td className="font-medium text-gray-900">{qf.dataField?.name || qf.fieldId}</td>
                      <td className="text-xs text-gray-500 font-mono">{qf.dataField?.key || '—'}</td>
                      <td><span className="badge badge-info">{fieldTypeLabel(qf.dataField?.type || 'text')}</span></td>
                      <td className="text-sm text-gray-700">
                        {qf.label || qf.dataField?.label || qf.dataField?.name || '—'}
                      </td>
                      <td>
                        {qf.dataField?.isRequired
                          ? <span className="badge badge-danger">Pflicht</span>
                          : <span className="badge">Optional</span>}
                      </td>
                      <td>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => reorder(idx, 'up')}
                            disabled={idx === 0}
                            className="btn-ghost p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                            title="Nach oben"
                          >
                            <MoveUp size={14} />
                          </button>
                          <button
                            onClick={() => reorder(idx, 'down')}
                            disabled={idx === (questionnaire.fields?.length ?? 1) - 1}
                            className="btn-ghost p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                            title="Nach unten"
                          >
                            <MoveDown size={14} />
                          </button>
                        </div>
                      </td>
                      <td>
                        <button onClick={() => { if (confirm('Feld "' + (qf.dataField?.name || '?') + '" aus Fragebogen entfernen?')) removeFieldMutation.mutate(qf.id); }}
                          className="btn-ghost p-1 text-gray-400 hover:text-red-500" title="Entfernen">
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ════════════ Einreichungen-Tab ════════════ */}
      {activeTab === 'submissions' && (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          {submissions.isLoading ? (
            <div className="flex items-center justify-center py-12"><Loader2 size={24} className="animate-spin text-gray-400" /></div>
          ) : !submissions.data || submissions.data.length === 0 ? (
            <div className="text-center py-10">
              <FileText size={40} className="mx-auto text-gray-300 mb-3" />
              <p className="text-sm text-gray-500">Noch keine Einreichungen</p>
            </div>
          ) : (
            <table className="data-table w-full">
              <thead>
                <tr>
                  <th>Datum & Uhrzeit</th>
                  <th>Eingereicht von</th>
                  <th>Organisation</th>
                  <th>Eingaben</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {submissions.data.map((entry: any) => (
                  <tr key={entry.id} className="hover:bg-gray-50 border-b border-gray-200 last:border-b-0">
                    <td className="text-sm whitespace-nowrap">
                      {entry.submittedAt ? format(new Date(entry.submittedAt), 'dd.MM.yyyy HH:mm:ss', { locale: de }) : '—'}
                    </td>
                    <td className="text-sm font-medium">
                      {entry.submittedBy?.displayName || (entry.submittedById === 'system_submitted' ? 'System (Auto-Aggregation)' : entry.submittedById || 'System')}
                    </td>
                    <td className="text-sm">{entry.organization?.name || '—'}</td>
                    <td className="text-sm max-w-xs">
                      {entry.dataEntries && entry.dataEntries.length > 0 ? (
                        <div className="space-y-0.5">
                          {entry.dataEntries.slice(0, 5).map((de: any, i: number) => (
                            <div key={de.id || i} className="flex gap-1 text-xs">
                              <span className="font-medium text-gray-600 shrink-0">{de.dataField?.name || de.dataField?.key || '?'}:</span>
                              <FormatValue de={de} navigate={navigate} />
                            </div>
                          ))}
                          {entry.dataEntries.length > 5 && (
                            <div className="text-xs text-gray-400 mt-1">+ {entry.dataEntries.length - 5} weitere</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">Keine Daten</span>
                      )}
                    </td>
                    <td>
                      {entry.isLate
                        ? <span className="badge badge-danger">Verspätet</span>
                        : <span className="badge badge-success">Rechtzeitig</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      {/* ════════════ Zuweisungen-Tab ════════════ */}
      {activeTab === 'assignments' && (
        <div className="space-y-6">
          {/* Organisationen */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                <Building2 size={18} /> Organisationen ({questionnaire.orgAssignments?.length ?? 0})
              </h3>
              <button onClick={() => setShowOrgAssignModal(true)} className="btn-secondary text-sm">
                <Plus size={14} /> Organisation zuweisen
              </button>
            </div>
            {(!questionnaire.orgAssignments || questionnaire.orgAssignments.length === 0) ? (
              <p className="text-sm text-gray-400 bg-gray-50 rounded-lg px-4 py-3">
                Keine Organisationen zugewiesen. <span className="text-amber-600 font-medium">⚠️ 1 Einreichung pro OE reicht.</span>
              </p>
            ) : (
              <div className="space-y-2">
                {questionnaire.orgAssignments.map((oa: any) => (
                  <div key={oa.id || oa.organization?.id} className="flex items-center justify-between bg-blue-50 rounded-lg px-4 py-3 border border-blue-100">
                    <div className="flex items-center gap-3">
                      <Building2 size={18} className="text-blue-600 shrink-0" />
                      <div>
                        <span className="font-medium text-gray-800">{oa.organization?.name || oa.organizationId}</span>
                        <span className="text-xs text-gray-500 ml-2">1 Einreichung pro OE</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">

                      <button onClick={() => { if (confirm('Zuweisung der Organisation ' + (oa.organization?.name || '') + ' entfernen?')) removeOrgMutation.mutate(oa.organization?.id || oa.organizationId); }}
                        className="btn-ghost p-1.5 text-gray-400 hover:text-red-500">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Nutzer */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                <Users size={18} /> Einzelne Mitarbeiter ({questionnaire.assignments?.length ?? 0})
              </h3>
              <button onClick={() => setShowUserAssignModal(true)} className="btn-secondary text-sm">
                <UserPlus size={14} /> Mitarbeiter zuweisen
              </button>
            </div>
            {(!questionnaire.assignments || questionnaire.assignments.length === 0) ? (
              <p className="text-sm text-gray-400 bg-gray-50 rounded-lg px-4 py-3">Keine Mitarbeiter direkt zugewiesen.</p>
            ) : (
              <div className="space-y-2">
                {questionnaire.assignments.map((a: any) => (
                  <div key={a.id || a.user?.id} className="flex items-center justify-between bg-green-50 rounded-lg px-4 py-3 border border-green-100">
                    <div className="flex items-center gap-3">
                      <Users size={18} className="text-green-600 shrink-0" />
                      <span className="font-medium text-gray-800">{a.user?.displayName || a.userId}</span>
                      <span className="text-xs text-gray-500">Muss individuell ausfüllen</span>
                    </div>
                    <button onClick={() => { if (confirm('Zuweisung von ' + (a.user?.displayName || '') + ' entfernen?')) removeUserMutation.mutate(a.user?.id || a.userId); }}
                      className="btn-ghost p-1.5 text-gray-400 hover:text-red-500">
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Direkter Link zum Fragebogen */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                <Link size={18} /> Direkter Link zum Fragebogen
              </h3>
            </div>
            <p className="text-sm text-gray-500 mb-3">
              Der Link öffnet nach Anmeldung direkt den Fragebogen
            </p>

            {!directLinkUrl ? (
              <p className="text-sm text-gray-400 bg-gray-50 rounded-lg px-4 py-3">
                {directLinkError ? 'Direktlink konnte nicht geladen werden.' : 'Direktlink wird geladen...'}
              </p>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center justify-between rounded-lg px-4 py-3 border bg-purple-50 border-purple-100">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <Link size={18} className="shrink-0 text-purple-600" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm text-gray-800 truncate max-w-[200px]">{directLinkUrl}</span>
                        <button onClick={() => { navigator.clipboard.writeText(directLinkUrl); }}
                          className="btn-ghost p-1 text-gray-400 hover:text-purple-600 shrink-0"
                          title="Link kopieren">
                          <Copy size={14} />
                        </button>
                        <button onClick={() => { setQrLink(directLinkUrl); setShowQrModal(true); }}
                          className="btn-ghost p-1 text-gray-400 hover:text-purple-600 shrink-0"
                          title="QR-Code anzeigen">
                          <QrCode size={14} />
                        </button>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-700">
                          Aktiv
                        </span>
                        <span className="text-xs text-gray-400">Anmeldung erforderlich</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Anonymer Link */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                <Link size={18} /> Anonymer Link
              </h3>
              <button onClick={() => setShowAnonLinkModal(true)} className="btn-secondary text-sm">
                <Link size={14} /> Link generieren
              </button>
            </div>

            {(!anonymousLinks || anonymousLinks.length === 0) ? (
              <p className="text-sm text-gray-400 bg-gray-50 rounded-lg px-4 py-3">
                Keine anonymen Links vorhanden. Generiere einen Link, um den Fragebogen ohne Anmeldung ausfüllen zu lassen.
              </p>
            ) : (
              <div className="space-y-2">
                {anonymousLinks.map((alink: any) => {
                  const linkUrl = window.location.origin + '/fill/' + alink.token + '/anonymous';
                  const isExpired = alink.expiresAt && new Date(alink.expiresAt) < new Date();
                  return (
                    <div key={alink.id} className={'flex items-center justify-between rounded-lg px-4 py-3 border ' + (alink.isActive && !isExpired ? 'bg-purple-50 border-purple-100' : 'bg-gray-100 border-gray-200 opacity-60')}>
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <Link size={18} className={'shrink-0 ' + (alink.isActive && !isExpired ? 'text-purple-600' : 'text-gray-400')} />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm text-gray-800 truncate max-w-[200px]">{linkUrl}</span>
                            <button onClick={() => { navigator.clipboard.writeText(linkUrl); }}
                              className="btn-ghost p-1 text-gray-400 hover:text-purple-600 shrink-0"
                              title="Link kopieren">
                              <Copy size={14} />
                            </button>
                            <button onClick={() => { setQrLink(linkUrl); setShowQrModal(true); }}
                              className="btn-ghost p-1 text-gray-400 hover:text-purple-600 shrink-0"
                              title="QR-Code anzeigen">
                              <QrCode size={14} />
                            </button>
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className={'text-xs px-1.5 py-0.5 rounded ' + (alink.isActive && !isExpired ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700')}>
                              {alink.isActive && !isExpired ? 'Aktiv' : isExpired ? 'Abgelaufen' : 'Deaktiviert'}
                            </span>
                            {alink.expiresAt && (
                              <span className="text-xs text-gray-400 flex items-center gap-1">
                                <Clock size={10} /> {new Date(alink.expiresAt).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              </span>
                            )}
                            {!alink.expiresAt && (
                              <span className="text-xs text-gray-400">Kein Ablauf</span>
                            )}
                          </div>
                        </div>
                      </div>
                      {alink.isActive && !isExpired && (
                        <button onClick={() => { if (confirm('Link deaktivieren? Bereits begonnene Einreichungen sind nicht betroffen, aber der Link kann nicht mehr verwendet werden.')) deactivateAnonLinkMutation.mutate(alink.id); }}
                          className="btn-ghost p-1.5 text-gray-400 hover:text-red-500 shrink-0">
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="bg-gray-50 rounded-lg p-4 text-xs text-gray-500 space-y-1 border border-gray-100">
            <p><strong>Regeln:</strong></p>
            <p>• Organisationen-Zuweisung: <strong>1 Einreichung pro OE</strong> ausreichend. Reminder an <strong>alle Mitglieder</strong>.</p>
            <p>• Einzel-Zuweisung: <strong>Jeder zugewiesene Mitarbeiter</strong> muss ausfüllen. Reminder nur an diesen.</p>
            <p>• Gehört ein Mitarbeiter einer zugewiesenen OE <strong>und</strong> ist einzeln zugewiesen, erfüllt 1 Einreichung beide.</p>
            <p>• Zweite Einreichung eines Org-Kollegen überschreibt die erste (Korrektur-Modus).</p>
          </div>
        </div>
      )}

      {/* ──── Modals ──── */}

      {/* Org Assign Modal */}
      <Modal isOpen={showOrgAssignModal} onClose={() => setShowOrgAssignModal(false)} title="Organisation zuweisen">
        <div>
          <label className="label">Organisation auswählen</label>
          <select className="input-field" value={assignOrgId} onChange={(e) => setAssignOrgId(e.target.value)}>
            <option value="">Bitte wählen</option>
            {orgs.filter((o: any) => !assignedOrgIds.includes(o.id)).map((o: any) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
          {orgs.filter((o: any) => !assignedOrgIds.includes(o.id)).length === 0 && (
            <p className="text-xs text-gray-500 mt-1">Alle Organisationen bereits zugewiesen</p>
          )}
        </div>
        <div className="flex justify-end gap-3">
          <button onClick={() => setShowOrgAssignModal(false)} className="btn-secondary">Abbrechen</button>
          <button onClick={() => assignOrgMutation.mutate()} disabled={!assignOrgId || assignOrgMutation.isPending} className="btn-primary">
            {assignOrgMutation.isPending ? <><Loader2 size={16} className="animate-spin" /> Zuweisen...</> : 'Zuweisen'}
          </button>
        </div>
      </Modal>

      {/* User Assign Modal */}
      <Modal isOpen={showUserAssignModal} onClose={() => setShowUserAssignModal(false)} title="Mitarbeiter zuweisen">
        <div>
          <label className="label">Mitarbeiter auswählen</label>
          <select className="input-field" value={assignUserId} onChange={(e) => setAssignUserId(e.target.value)}>
            <option value="">Bitte wählen</option>
            {users.filter((u: any) => !assignedUserIds.includes(u.id)).map((u: any) => (
              <option key={u.id} value={u.id}>{u.displayName || u.username || u.email}</option>
            ))}
          </select>
          {users.filter((u: any) => !assignedUserIds.includes(u.id)).length === 0 && (
            <p className="text-xs text-gray-500 mt-1">Alle Benutzer bereits zugewiesen</p>
          )}
        </div>
        <div className="flex justify-end gap-3">
          <button onClick={() => setShowUserAssignModal(false)} className="btn-secondary">Abbrechen</button>
          <button onClick={() => assignUserMutation.mutate()} disabled={!assignUserId || assignUserMutation.isPending} className="btn-primary">
            {assignUserMutation.isPending ? <><Loader2 size={16} className="animate-spin" /> Zuweisen...</> : 'Zuweisen'}
          </button>
        </div>
      </Modal>

      {/* Field Assign Modal */}
      <Modal isOpen={showAssignModal} onClose={() => setShowAssignModal(false)} title="Feld zuweisen">
        <div>
          <label className="label">Datenfeld auswählen</label>
          <select className="input-field" value={assignFieldId} onChange={(e) => setAssignFieldId(e.target.value)}>
            <option value="">Bitte wählen</option>
            {availableFields.map((f: any) => (
              <option key={f.id} value={f.id}>{f.label || f.key || f.name} ({fieldTypeLabel(f.type)})</option>
            ))}
          </select>
          {availableFields.length === 0 && <p className="text-xs text-gray-500 mt-1">Keine weiteren Datenfelder verfügbar</p>}
        </div>
        <div className="flex justify-end gap-3">
          <button onClick={() => setShowAssignModal(false)} className="btn-secondary">Abbrechen</button>
          <button onClick={() => { if (assignFieldId) addFieldMutation.mutate(assignFieldId); }}
            disabled={!assignFieldId || addFieldMutation.isPending} className="btn-primary">
            {addFieldMutation.isPending ? <><Loader2 size={16} className="animate-spin" /> Zuweisen...</> : 'Hinzufügen'}
          </button>
        </div>
      </Modal>

      {/* ─── Anonymer Link erstellen Modal ─── */}
      <Modal isOpen={showAnonLinkModal} onClose={() => setShowAnonLinkModal(false)} title="Anonymen Link generieren">
        <div className="space-y-4">
          <p className="text-sm text-gray-500">
            Generiere einen Link, der ohne Anmeldung Zugriff auf diesen Fragebogen bietet.
          </p>

          <div>
            <label className="label">Ablaufdatum (optional)</label>
            <div className="flex gap-2">
              <input
                type="date"
                className="input-field flex-1"
                value={anonExpiry}
                onChange={(e) => setAnonExpiry(e.target.value)}
              />
              <input
                type="time"
                className="input-field w-36"
                value={anonExpiryTime}
                onChange={(e) => setAnonExpiryTime(e.target.value)}
              />
            </div>
            <p className="text-xs text-gray-400 mt-1">Leer lassen = dauerhaft gültig. Das Einreichungslimit des Fragebogens gilt auch für anonyme Einreichungen.</p>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button onClick={() => setShowAnonLinkModal(false)} className="btn-secondary">Abbrechen</button>
            <button onClick={() => createAnonLinkMutation.mutate()} disabled={createAnonLinkMutation.isPending} className="btn-primary">
              {createAnonLinkMutation.isPending ? <><Loader2 size={16} className="animate-spin" /> Generieren...</> : 'Link erstellen'}
            </button>
          </div>
        </div>
      </Modal>

      {/* ─── QR-Code Modal ─── */}
      <Modal isOpen={showQrModal} onClose={() => setShowQrModal(false)} title="QR-Code">
        <div className="flex flex-col items-center gap-4 py-4">
          {qrLink && (
            <>
              <img
                src={"https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=" + encodeURIComponent(qrLink)}
                alt="QR-Code"
                className="rounded-lg border border-gray-200"
                style={{ width: 250, height: 250 }}
              />
              <p className="text-xs text-gray-500 text-center break-all max-w-xs">{qrLink}</p>
              <button
                onClick={() => { navigator.clipboard.writeText(qrLink); }}
                className="btn-secondary text-sm flex items-center gap-2"
              >
                <Copy size={14} /> Link kopieren
              </button>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}

// ─── Inline SVG Icons ───

function Archive({ size, className }: { size?: number; className?: string }) {
  return (
    <svg width={size ?? 24} height={size ?? 24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <rect x="2" y="3" width="20" height="5" rx="1" /><path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8" /><path d="M10 12h4" />
    </svg>
  );
}
function Eye({ size, className }: { size?: number; className?: string }) {
  return (
    <svg width={size ?? 24} height={size ?? 24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" />
    </svg>
  );
}
